import api from '../../../shared/services/api';
import { API_ENDPOINTS } from '../../../shared/constants/apiEndpoints';

/**
 * Service for managing FMO generation (groups, clusters, entries, downloads)
 */
export const fmoGeneratorService = {
  /**
   * Create a group for FMO processing
   * @param {Array} prepIds - Array of prep IDs
   * @param {string} target - Target name
   * @returns {Promise<Object>} Created group object
   */
  async createGroup(prepIds, target) {
    const groupData = [{
      prep_ids: prepIds,
      auto_align: false,
      tags: [`target:${target}`, `date:${new Date().toISOString().split('T')[0]}`]
    }];
    
    let groupRes = await api.post(API_ENDPOINTS.GROUPS.BASE, groupData);
    
    // Poll if 202 Accepted
    let pollCount = 0;
    const maxPolls = 60;
    const pollInterval = 1000;
    
    while (groupRes.status === 202 && pollCount < maxPolls) {
      pollCount++;
      await new Promise(resolve => setTimeout(resolve, pollInterval));
      groupRes = await api.post(API_ENDPOINTS.GROUPS.BASE, groupData);
    }
    
    if (groupRes.status === 202) {
      throw new Error('Group creation timeout');
    }
    
    // Extract group data from response
    if (groupRes.data.workflow && groupRes.data.workflow.result && groupRes.data.workflow.result.items) {
      return groupRes.data.workflow.result.items[0];
    } else if (Array.isArray(groupRes.data)) {
      return groupRes.data[0];
    } else {
      return groupRes.data;
    }
  },

  /**
   * Create a new cluster for FMO processing
   * @param {Object} params - Cluster creation parameters
   * @returns {Promise<number>} Created cluster ID
   */
  async createCluster({ name, clusterspecId, groupId, target }) {
    const clusterData = [{
      name: name,
      mode: 'auto',
      clusterspec_id: parseInt(clusterspecId),
      group_id: groupId,
      tags: [`target:${target}`, `clusterspec:${clusterspecId}`]
    }];
    
    let clusterRes = await api.post(API_ENDPOINTS.CLUSTERS.BASE, clusterData);
    
    // Poll if 202 Accepted
    let pollCount = 0;
    const maxPolls = 60;
    const pollInterval = 1000;
    
    while (clusterRes.status === 202 && pollCount < maxPolls) {
      pollCount++;
      await new Promise(resolve => setTimeout(resolve, pollInterval));
      clusterRes = await api.post(API_ENDPOINTS.CLUSTERS.BASE, clusterData);
    }
    
    if (clusterRes.status === 202) {
      throw new Error('Cluster creation timeout');
    }
    
    // Extract cluster ID from response
    if (clusterRes.data.workflow && clusterRes.data.workflow.result && clusterRes.data.workflow.result.items) {
      return clusterRes.data.workflow.result.items[0].id_;
    } else if (Array.isArray(clusterRes.data)) {
      return clusterRes.data[0].id_;
    } else {
      return clusterRes.data.id_;
    }
  },

  /**
   * Create FMO entries
   * @param {Object} params - Entry creation parameters
   * @returns {Promise<Array>} Created entries
   */
  async createEntries({ groupMembers, clusterId, pocketspecId, inputspecId, target, configIndex }) {
    // Create entries for this configuration
    let entryDataArray = groupMembers.map(member => ({
      align_id: member.align_id,
      cluster_id: parseInt(clusterId),
      pocketspec_id: parseInt(pocketspecId),
      ligand_id: member.ligand_id,
      inputspec_id: parseInt(inputspecId),
      tags: [
        `target:${target}`, 
        `prep_id:${member.prep_id}`, 
        `config:${configIndex + 1}`,
        `date:${new Date().toISOString().split('T')[0]}`
      ]
    }));
    
    // If no members info, create a single entry (fallback)
    if (entryDataArray.length === 0) {
      console.warn('No group members found, creating single entry');
      entryDataArray = [{
        align_id: 1,
        cluster_id: parseInt(clusterId),
        pocketspec_id: parseInt(pocketspecId),
        ligand_id: 1,
        inputspec_id: parseInt(inputspecId),
        tags: [
          `target:${target}`,
          `config:${configIndex + 1}`,
          `date:${new Date().toISOString().split('T')[0]}`
        ]
      }];
    }
    
    let entryRes = await api.post(API_ENDPOINTS.ENTRIES.BASE, entryDataArray);
    
    // Poll if 202 Accepted
    let pollCount = 0;
    const maxPolls = 900; // 15 minutes timeout (900 seconds)
    const pollInterval = 1000;
    
    while (entryRes.status === 202 && pollCount < maxPolls) {
      pollCount++;
      await new Promise(resolve => setTimeout(resolve, pollInterval));
      entryRes = await api.post(API_ENDPOINTS.ENTRIES.BASE, entryDataArray);
      
      // Add logging for intermediate responses
      console.log(`[Poll ${pollCount}/${maxPolls}] Entry creation status:`, {
        status: entryRes.status,
        statusText: entryRes.statusText,
        data: entryRes.data,
        workflow: entryRes.data?.workflow,
        progress: entryRes.data?.workflow?.progress,
        state: entryRes.data?.workflow?.state
      });
    }
    
    if (entryRes.status === 202) {
      throw new Error('Entry creation timeout');
    }
    
    // Extract entries from response
    if (entryRes.data.workflow && entryRes.data.workflow.result && entryRes.data.workflow.result.items) {
      return entryRes.data.workflow.result.items;
    } else if (Array.isArray(entryRes.data)) {
      return entryRes.data;
    } else {
      return [entryRes.data];
    }
  },

  /**
   * Generate default cluster name
   * @param {Object} params - Parameters for name generation
   * @returns {string} Generated cluster name
   */
  generateDefaultClusterName({ target, files, clusterspecName }) {
    const today = new Date().toISOString().slice(2, 10).replace(/-/g, '').slice(0, 6);
    const targetRows = files;
    const batches = [...new Set(
      targetRows
        .map(row => row.batch)
        .filter(Boolean)
        .filter(b => b !== '<none>')
    )];
    
    const batchPart = batches.length === 1 && batches[0] 
      ? batches[0] 
      : batches.length > 1 
        ? 'mixed' 
        : `${targetRows.length}files`;
    
    return `${today}_${target}_${batchPart}_${clusterspecName}`;
  },

  /**
   * Generate default zipfile name based on file metadata
   * @param {Array} selectedRows - Selected prep files
   * @returns {string} Default zipfile name
   */
  generateZipfileName(selectedRows) {
    if (!selectedRows || selectedRows.length === 0) {
      return 'fmo_inputs.zip';
    }

    const parts = [];
    
    // Add today's date (not prepared_at)
    const today = new Date().toISOString().slice(2, 10).replace(/-/g, '').slice(0, 6);
    parts.push(today);
    
    // Helper function to check field consistency
    const getFieldValue = (fieldValues) => {
      // Get unique non-empty values
      const uniqueValues = [...new Set(fieldValues.filter(Boolean))];
      
      // Check if any rows have empty values
      const hasEmptyValues = fieldValues.some(v => !v || v === '<none>');
      
      if (uniqueValues.length === 0) {
        // All values are empty
        return null;
      } else if (uniqueValues.length === 1 && !hasEmptyValues) {
        // Single value for all rows
        return uniqueValues[0];
      } else {
        // Multiple values OR single value with some empty rows
        return 'various';
      }
    };
    
    // Check target field
    const targetValue = getFieldValue(selectedRows.map(row => row.target));
    if (targetValue === 'various') {
      parts.push('various-targets');
    } else if (targetValue) {
      parts.push(targetValue);
    }
    
    // Check mutation field
    const mutationValue = getFieldValue(selectedRows.map(row => row.mutation));
    if (mutationValue === 'various') {
      parts.push('various-mutations');
    } else if (mutationValue) {
      parts.push(mutationValue);
    }
    
    // Check subgroup field
    const subgroupValue = getFieldValue(selectedRows.map(row => row.subgroup));
    if (subgroupValue === 'various') {
      parts.push('various-subgroups');
    } else if (subgroupValue) {
      parts.push(subgroupValue);
    }
    
    // Check workflow field  
    const workflowValue = getFieldValue(selectedRows.map(row => row.prep_workflow));
    if (workflowValue === 'various') {
      parts.push('various-workflows');
    } else if (workflowValue) {
      parts.push(workflowValue);
    }
    
    // Check batch field (special handling for '<none>' value)
    const batchValues = selectedRows.map(row => {
      const batch = row.batch;
      return (batch && batch !== '<none>') ? batch : null;
    });
    const batchValue = getFieldValue(batchValues);
    if (batchValue === 'various') {
      parts.push('various-batches');
    } else if (batchValue) {
      parts.push(batchValue);
    }
    
    return parts.join('_') + '.zip';
  },

  /**
   * Create a zipfile with generated entries
   * @param {Array} entryIds - Array of entry IDs to include
   * @param {string} name - Name for the zipfile
   * @returns {Promise<Object>} Created zipfile object
   */
  async createZipfile(entryIds, name) {
    if (!entryIds || entryIds.length === 0) {
      throw new Error('No entry IDs provided for zipfile');
    }
    
    if (!name) {
      throw new Error('Zipfile name is required');
    }
    
    // Remove .zip extension if present (case-insensitive)
    const cleanName = name.replace(/\.zip$/i, '');
    
    const response = await api.post('/zipfiles', [{
      name: cleanName,
      entry_ids: entryIds
    }]);
    
    // Log for debugging (can be removed in production)
    // console.log('Zipfile creation response:', response.data);
    
    // The API returns a workflow response with the zipfile in workflow.result.items[0]
    if (response.data && response.data.workflow && response.data.workflow.result && 
        response.data.workflow.result.items && response.data.workflow.result.items.length > 0) {
      return response.data.workflow.result.items[0];
    } else if (Array.isArray(response.data) && response.data.length > 0) {
      // Fallback for direct array response
      return response.data[0];
    } else if (response.data && response.data.id_) {
      // Fallback for direct object response
      return response.data;
    } else {
      throw new Error('Unexpected response format from zipfile creation');
    }
  },

  /**
   * Download a zipfile by ID
   * @param {number} zipfileId - Zipfile ID to download
   * @param {string} filename - Filename for download
   * @returns {Promise<void>}
   */
  async downloadZipfile(zipfileId, filename) {
    // Use the base endpoint for downloading zipfile
    const response = await api.get(
      `/zipfiles/${zipfileId}`,
      {
        responseType: 'blob' // Important for downloading files
      }
    );
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    
    // Use provided filename or fallback
    link.setAttribute('download', filename || 'fmo_inputs.zip');
    document.body.appendChild(link);
    link.click();
    link.remove();
    
    // Clean up
    window.URL.revokeObjectURL(url);
  },

  /**
   * Legacy method for backward compatibility - creates zipfile and downloads it
   * @param {Array} entryIds - Array of entry IDs to download
   * @param {string} zipfileName - Optional name for the zipfile
   * @param {Array} selectedRows - Selected prep files for generating default name
   * @returns {Promise<void>}
   */
  async downloadEntriesAsZip(entryIds, zipfileName = null, selectedRows = []) {
    if (!entryIds || entryIds.length === 0) {
      throw new Error('No entry IDs provided for download');
    }
    
    // If no name provided, generate default
    const name = zipfileName || this.generateZipfileName(selectedRows);
    
    // Create zipfile first
    const zipfile = await this.createZipfile(entryIds, name);
    
    // Then download it
    await this.downloadZipfile(zipfile.id_, name);
  },

  /**
   * Submit FMO jobs
   * @param {Array} entryIds - Array of entry IDs to submit as jobs
   * @returns {Promise<Object>} Job submission response
   */
  async submitJobs(entryIds) {
    if (!entryIds || entryIds.length === 0) {
      throw new Error('No entry IDs provided for job submission');
    }
    
    const jobData = entryIds.map(entryId => ({
      entry_id: entryId,
      tags: [`submitted:${new Date().toISOString().split('T')[0]}`]
    }));
    
    return await api.post(API_ENDPOINTS.JOBS.BASE, jobData);
  }
};